// ============================================================================
// HR PIN SIDEBAR - Updated with Checkout Requests + Anomaly Detector
// Path: frontend/src/components/hr-pin/HrPinSidebar.tsx
// ============================================================================

import React, { useMemo } from 'react';
import { Layout, Menu, Avatar, Typography, Divider } from 'antd';
import {
  DashboardOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  FieldTimeOutlined,
  CheckCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ExceptionOutlined,
  WarningOutlined         // 🆕 Anomaly Detector icon
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { AuthUser } from '../../services/auth.service';
import styles from './HrPinSidebar.module.css';


const { Sider } = Layout;
const { Text } = Typography;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface HrPinSidebarProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  user: AuthUser;
}

// ============================================================================
// MENU ITEMS CONFIGURATION
// ============================================================================

const menuItems: MenuProps['items'] = [
  {
    key: 'dashboard',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
  },
  {
    key: 'attendance',
    icon: <ClockCircleOutlined />,
    label: 'PTS-Attendance',
  },
  {
    key: 'hr-payroll-report',
    icon: <FileTextOutlined />,
    label: 'PTS-HR-Payroll',
  },
  {
    key: 'overtime-reports',
    icon: <FieldTimeOutlined />,
    label: 'PTS-Overtime',
  },
  {
    key: 'checkout-requests',
    icon: <ExceptionOutlined />,
    label: 'Checkout Requests',
  },

  {

    key: 'salary-slip-requests',
    icon: <FileTextOutlined />,
    label: 'Salary Slips',

    },
  {
    key: 'attendance-anomaly-detector',      // 🆕 NEW
    icon: <WarningOutlined />,
    label: 'Anomaly Detector',
  },
  {
    key: 'hr-leave-approvals',
    icon: <CheckCircleOutlined />,
    label: 'Approvals',
  },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const HrPinSidebar: React.FC<HrPinSidebarProps> = ({
  activeTab,
  onTabChange,
  collapsed,
  onCollapse,
  user
}) => {
  const userInitials = useMemo(() => {
    const name = user.employeeName || user.name || 'HR';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }, [user]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    onTabChange(key);
  };

  return (
    <Sider
      className={styles.sidebar}
      width={260}
      collapsedWidth={80}
      collapsed={collapsed}
      onCollapse={onCollapse}
      theme="light"
      trigger={null}
    >
      <div 
        className={styles.collapseButton}
        onClick={() => onCollapse(!collapsed)}
      >
        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </div>

      {!collapsed && (
        <div className={styles.userProfile}>
          <Avatar 
            size={48} 
            style={{ backgroundColor: '#4A90E2' }}
          >
            {userInitials}
          </Avatar>
          <div className={styles.userDetails}>
            <Text className={styles.userName} ellipsis>
              {user.employeeName || user.name || 'HR Manager'}
            </Text>
            <Text className={styles.userRole} ellipsis>
              {user.designation || 'HR & Office Manager'}
            </Text>
          </div>
        </div>
      )}

      {collapsed && (
        <div className={styles.userProfileCollapsed}>
          <Avatar 
            size={40} 
            style={{ backgroundColor: '#4A90E2' }}
          >
            {userInitials}
          </Avatar>
        </div>
      )}

      <Divider className={styles.divider} />

      <Menu
        mode="inline"
        selectedKeys={[activeTab]}
        onClick={handleMenuClick}
        items={menuItems}
        className={styles.menu}
      />

      {!collapsed && (
        <div className={styles.sidebarFooter}>
          <Text className={styles.footerText}>
            Employee #{user.employeeNumber || '-'}
          </Text>
          <Text className={styles.footerText}>
            {user.department || 'HR Department'}
          </Text>
        </div>
      )}
    </Sider>
  );
};

export default HrPinSidebar;